import React from 'react';
import { Button, Empty, List, Spin, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Linkedin,
  AlertTriangle,
  Bell,
  Building2,
  CalendarCheck,
  CheckCheck,
  Clock,
  CreditCard,
  Layers,
  MailOpen,
  MousePointerClick,
  Phone,
  UserPlus,
  Users,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import {
  useDismissNotification,
  useMarkAllAsRead,
  useMarkAsRead,
  useNotifications,
  type PlatformNotification,
  type PlatformNotificationKind,
} from '../../services/notificationsApi';
import { BRAND_PRIMARY } from '../../constants/brand';
import { useHasAdminAccess } from '../../hooks/useHasAccess';

const { Text } = Typography;

// D3 W21 colour tokens (kept inline so a future Tailwind/AntD theme refactor
// can grep them in one place). Semantic mapping:
//   danger  — hard lifecycle stop (suspended, cancelled, payment_failed)
//   warning — recoverable, action recommended (trial_ending, credit_low)
//   success — positive lifecycle event (subscription_renewed)
//   info    — neutral notable event (plan_changed)
//   neutral — back-compat brand-indigo for existing kinds
//   gray    — soft-fade lifecycle close (subscription_cancelled, seat_removed)
//   blue    — operations action (seat_added)
const COLOR_DANGER = '#DC2626';
const COLOR_WARNING = '#D97706';
const COLOR_SUCCESS = '#16A34A';
const COLOR_INFO = '#2563EB';
const COLOR_GRAY = '#6B7280';

const KIND_ICON: Record<PlatformNotificationKind, React.ReactNode> = {
  access_request_pending: <UserPlus size={14} />,
  access_request_approved: <CheckCheck size={14} />,
  access_request_rejected: <CheckCheck size={14} />,
  tenant_created: <Building2 size={14} />,
  invite_expired: <Clock size={14} />,
  subscription_suspended: <CreditCard size={14} />,
  subscription_cancelled: <CreditCard size={14} />,
  payment_failed: <CreditCard size={14} />,
  // D3 W21 / arch#21 — see janus-billing/clients/notification_client.py.
  subscription_renewed: <CreditCard size={14} />,
  seat_added: <Users size={14} />,
  seat_removed: <Users size={14} />,
  plan_changed: <Layers size={14} />,
  trial_ending: <Clock size={14} />,
  credit_low: <AlertTriangle size={14} />,
  // RP-01 — contact reveal lifecycle.
  contact_revealed: <Phone size={14} />,
  contact_reveal_failed: <Phone size={14} />,
  // SDR — AI-booked meeting awaiting assignment.
  meeting_pending_assignment: <CalendarCheck size={14} />,
  // Nurture-régua engagement.
  nurture_email_opened: <MailOpen size={14} />,
  nurture_email_clicked: <MousePointerClick size={14} />,
  // Envio LinkedIn fire-and-forget (desfechos acionáveis).
  linkedin_send_failed: <Linkedin size={14} />,
  linkedin_send_blocked: <Linkedin size={14} />,
  linkedin_session_challenge: <Linkedin size={14} />,
  generic: <Bell size={14} />,
};

// Per-kind tint applied to the leading icon. Defaults to brand-indigo for
// neutral kinds; danger (red) for hard lifecycle stops; warning (amber)
// for recoverable events the user should act on but isn't blocked yet.
const KIND_ICON_COLOR: Record<PlatformNotificationKind, string> = {
  access_request_pending: BRAND_PRIMARY,
  access_request_approved: BRAND_PRIMARY,
  access_request_rejected: BRAND_PRIMARY,
  tenant_created: BRAND_PRIMARY,
  invite_expired: BRAND_PRIMARY,
  subscription_suspended: COLOR_DANGER,
  subscription_cancelled: COLOR_GRAY,
  payment_failed: COLOR_DANGER,
  // D3 W21 / arch#21
  subscription_renewed: COLOR_SUCCESS,
  seat_added: COLOR_INFO,
  seat_removed: COLOR_GRAY,
  plan_changed: COLOR_INFO,
  trial_ending: COLOR_WARNING,
  credit_low: COLOR_WARNING,
  // RP-01 — reveal success uses brand-indigo (positive lifecycle), and
  // reveal_failed uses gray to signal "nothing happened, no cobrança".
  contact_revealed: BRAND_PRIMARY,
  contact_reveal_failed: COLOR_GRAY,
  // SDR — neutral notable event (a meeting needs to be assigned).
  meeting_pending_assignment: BRAND_PRIMARY,
  // Nurture engagement — positive signal, brand-indigo.
  nurture_email_opened: BRAND_PRIMARY,
  nurture_email_clicked: BRAND_PRIMARY,
  // Envio LinkedIn — falha real (vermelho), bloqueio/atenção (âmbar).
  linkedin_send_failed: COLOR_DANGER,
  linkedin_send_blocked: COLOR_WARNING,
  linkedin_session_challenge: COLOR_WARNING,
  generic: BRAND_PRIMARY,
};

// Billing notification kinds — restricted to admin/account_owner. Operational
// users would otherwise click through to a 403 page.
const BILLING_KINDS: Set<PlatformNotificationKind> = new Set([
  'subscription_suspended',
  'subscription_cancelled',
  'payment_failed',
  // D3 W21 — every new billing kind also belongs in this set so we
  // gate click-through for non-admin users (defense-in-depth on top of
  // the BFF/billing tier check).
  'subscription_renewed',
  'seat_added',
  'seat_removed',
  'plan_changed',
  'trial_ending',
  'credit_low',
]);

// Default click-through when the backend didn't supply an action_url.
// Lets the publisher omit action_url for billing kinds and still route
// the user to the right place.
const KIND_DEFAULT_ACTION_URL: Partial<Record<PlatformNotificationKind, string>> = {
  subscription_suspended: '/admin/billing',
  subscription_cancelled: '/admin/billing',
  payment_failed: '/admin/billing',
  // D3 W21 / arch#21
  subscription_renewed: '/admin/billing',
  seat_added: '/admin/seats',
  seat_removed: '/admin/seats',
  plan_changed: '/admin/billing',
  trial_ending: '/admin/billing',
  credit_low: '/admin/billing',
};

function formatTimeAgo(dateStr: string, t: TFunction, locale: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMin < 1) return t('notifications.timeAgo.now', 'agora');
  if (diffMin < 60) return t('notifications.timeAgo.minutes', { count: diffMin, defaultValue: 'há {{count}}min' });
  if (diffHours < 24) return t('notifications.timeAgo.hours', { count: diffHours, defaultValue: 'há {{count}}h' });
  if (diffDays < 7) return t('notifications.timeAgo.days', { count: diffDays, defaultValue: 'há {{count}}d' });
  return date.toLocaleDateString(locale || 'pt-BR');
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const PlatformNotificationsTab: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const canAccessBilling = useHasAdminAccess();
  const { data: items = [], isLoading } = useNotifications(open, false, 20);
  const markRead = useMarkAsRead();
  const markAllRead = useMarkAllAsRead();
  const dismiss = useDismissNotification();

  const handleClick = (n: PlatformNotification) => {
    // Defensive: an operational user could otherwise click a billing
    // notification and land on a 403. Mark as read but skip navigation.
    if (BILLING_KINDS.has(n.kind) && !canAccessBilling) {
      if (!n.read) markRead.mutate(n.id);
      return;
    }
    if (!n.read) markRead.mutate(n.id);
    const target = n.action_url ?? KIND_DEFAULT_ACTION_URL[n.kind] ?? null;
    // Whitelist: only navigate to internal router paths. Reject absolute URLs
    // (e.g. https://evil.com) sent by a misbehaving notification publisher.
    if (target?.startsWith('/')) {
      onClose();
      navigate(target);
    }
    // else ignore — never navigate to absolute URLs from notifications
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 400 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '6px 12px',
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <Button
          type="link"
          size="small"
          loading={markAllRead.isPending}
          onClick={() => markAllRead.mutate()}
          aria-label={t('notifications.markAllAsRead', 'Marcar todas como lidas')}
          style={{ fontSize: 12, padding: 0, color: BRAND_PRIMARY }}
        >
          {t('notifications.markAllAsRead', 'Marcar todas como lidas')}
        </Button>
      </div>
      <div style={{ overflow: 'auto', flex: 1 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spin size="small" />
          </div>
        ) : items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('notifications.empty', 'Nenhuma notificação por enquanto')}
            style={{ padding: '24px 0' }}
          />
        ) : (
          <List
            dataSource={items}
            renderItem={(n) => {
              const titleText = n.title || t(`notifications.kinds.${n.kind}`, n.kind);
              // Fallback description for billing kinds when the publisher
              // didn't supply a body — keeps the surface informative without
              // forcing every backend caller to spell it out.
              const descriptionText =
                n.body || t(`notifications.kindDescriptions.${n.kind}`, '');
              return (
              <List.Item
                key={n.id}
                onClick={() => handleClick(n)}
                aria-label={titleText}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  background: n.read ? 'transparent' : '#F8FAFC',
                  borderBottom: '1px solid #F1F5F9',
                  alignItems: 'flex-start',
                }}
                actions={[
                  <Button
                    key="dismiss"
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismiss.mutate(n.id);
                    }}
                    aria-label={t('notifications.dismiss', 'Dispensar')}
                    style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}
                  >
                    {t('notifications.dismiss', 'Dispensar')}
                  </Button>,
                ]}
              >
                <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'flex-start' }}>
                  <span
                    aria-hidden="true"
                    style={{
                      color: KIND_ICON_COLOR[n.kind] ?? KIND_ICON_COLOR.generic,
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    {KIND_ICON[n.kind] ?? KIND_ICON.generic}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {titleText}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                        {formatTimeAgo(n.created_at, t, i18n.language)}
                      </Text>
                    </div>
                    {descriptionText && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 12,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: '18px',
                        }}
                      >
                        {descriptionText}
                      </Text>
                    )}
                  </div>
                </div>
              </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PlatformNotificationsTab;
